/** Webflow site timezone (Site settings → General). Editors enter incident dates in this zone. */
export const SITE_TIME_ZONE = 'America/New_York'

/** Editorial convention: an incident with no known time is entered as 1:00 pm Eastern. */
const NO_TIME_PLACEHOLDER = { hour: 13, minute: 0 }

/**
 * Whether a CMS DateTime carries a real time of day.
 *
 * Two values mean "no specific time given" and never get an hour-level display:
 * - 1:00 pm in the site timezone, the convention editors and the import use
 * - exactly midnight UTC, which is what a date-only value becomes through the API or a CSV
 */
export function hasTimeOfDay(iso: string): boolean {
  if (!iso) return false
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return false
  if (isMidnightUTC(parsed)) return false

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SITE_TIME_ZONE,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(parsed)
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  const isPlaceholder =
    part('hour') === NO_TIME_PLACEHOLDER.hour &&
    part('minute') === NO_TIME_PLACEHOLDER.minute &&
    part('second') === 0 &&
    parsed.getUTCMilliseconds() === 0
  return !isPlaceholder
}

function isMidnightUTC(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  )
}

/**
 * The calendar day an incident belongs to, as a Date at local midnight in the runtime's zone
 * (safe to hand to date-fns `format` without slipping a day).
 *
 * - date-only values (midnight UTC) keep their UTC calendar day
 * - everything else uses the day it was in the site's timezone, so an 11pm Eastern
 *   incident does not roll over to tomorrow just because it is already tomorrow in UTC
 */
export function toCalendarDate(iso: string): Date | null {
  if (!iso) return null
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null

  if (isMidnightUTC(parsed)) {
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SITE_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(parsed)
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return new Date(part('year'), part('month') - 1, part('day'))
}
